import type { QueryClient } from '@tanstack/react-query';
import { cleanThumbnailStorage, loadThumbnailBytes, removeThumbnailBytes } from './share-thumbnail-bytes';

export const SHARE_THUMBNAIL_CACHE_ROOT = 'share-thumbnail-cache';
export const MAX_CACHED_SHARE_THUMBNAILS = 200;
const RETRY_MS = 110_000;
const MIN_RETRY_MS = 5_000;

export type CachedShareThumbnail = {
  url: string | null;
  expiresAt: number;
  retryAt: number;
  requestedAt: number;
  version?: string;
  bytes?: number;
};
export type ShareThumbnailDescriptor = {
  url: string; expiresInSeconds: number; version?: string; contentUrl?: string;
};

/**
 * Authorized descriptors gate access to account-scoped persisted image bytes.
 * Blob URLs survive signed-link expiry; only metadata needs periodic refresh.
 */
export class ShareThumbnailCache {
  private entries = new Map<number, CachedShareThumbnail>();
  private listeners = new Set<() => void>();
  private timer?: ReturnType<typeof setTimeout>;
  private unsubscribe: () => void;
  private disposed = false;
  private downloads = new Map<number, { controller: AbortController; version: string }>();
  private queue: Array<() => Promise<void>> = [];
  private loading = 0;
  epoch = 0;

  constructor(private client: QueryClient, readonly scope: string) {
    // A bridge makes explicit invalidation and QueryClient.clear observable even
    // when only Latest Previews (and no table summary query) has been mounted.
    client.getQueryCache().build(client, {
      queryKey: [SHARE_THUMBNAIL_CACHE_ROOT, scope], gcTime: Infinity,
    });
    this.unsubscribe = client.getQueryCache().subscribe(event => {
      const root = event.query.queryKey[0];
      if (event.type === 'updated' && event.action.type === 'invalidate' &&
          (root === SHARE_THUMBNAIL_CACHE_ROOT ||
           (typeof root === 'string' && root.startsWith('/api/preview-shares/by-pulse')))) {
        this.invalidate();
        // Allow another mutation during an in-flight renewal to invalidate again.
        event.query.setState({ isInvalidated: false });
      }
      if (event.type === 'removed' &&
          (root === SHARE_THUMBNAIL_CACHE_ROOT || root === '/api/auth/me')) this.invalidate();
    });
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    this.armExpiry();
    return () => {
      this.listeners.delete(listener);
      this.armExpiry();
    };
  };

  read(id: number): CachedShareThumbnail | undefined {
    const entry = this.entries.get(id);
    if (!entry) return;
    // Touch on access for bounded LRU retention, including negative results.
    this.entries.delete(id);
    this.entries.set(id, entry);
    return entry;
  }

  url(id: number): string | null {
    const entry = this.read(id);
    return entry && Date.now() < entry.expiresAt ? entry.url : null;
  }

  put(id: number, thumbnail: ShareThumbnailDescriptor | undefined,
      requestedAt: number, epoch: number): boolean {
    if (this.disposed || epoch !== this.epoch) return false;
    const current = this.entries.get(id);
    if (current && current.requestedAt > requestedAt) return false;
    if (!thumbnail || (current?.version && current.version !== thumbnail.version)) {
      void removeThumbnailBytes(this.scope, id);
    }
    const now = Date.now();
    let entry: CachedShareThumbnail = { url: null, expiresAt: 0, retryAt: now + RETRY_MS, requestedAt };
    if (thumbnail?.version && thumbnail.contentUrl) {
      const sameVersion = current?.version === thumbnail.version;
      entry = {
        url: sameVersion ? current.url : null,
        bytes: sameVersion ? current.bytes : undefined,
        expiresAt: Infinity, retryAt: now + RETRY_MS, requestedAt, version: thumbnail.version,
      };
    } else if (thumbnail) {
      if (!Number.isFinite(thumbnail.expiresInSeconds) || thumbnail.expiresInSeconds <= 0) {
        throw new Error('Preview thumbnail URL has an invalid expiry.');
      }
      // Start at request dispatch, not receipt; clamp to the server's 120s limit.
      const expiresAt = requestedAt + Math.min(120, thumbnail.expiresInSeconds) * 1000;
      const remaining = expiresAt - now;
      const margin = Math.min(10_000, Math.max(1_000, remaining * 0.2));
      entry = {
        url: remaining > MIN_RETRY_MS ? thumbnail.url : null,
        expiresAt,
        retryAt: Math.max(now + MIN_RETRY_MS, expiresAt - margin),
        requestedAt,
      };
    }
    if (current?.url !== entry.url) this.release(current);
    this.entries.delete(id);
    this.entries.set(id, entry);
    this.prune();
    if (thumbnail?.version && thumbnail.contentUrl && !entry.url) {
      this.download(id, thumbnail.version, thumbnail.contentUrl, epoch);
    }
    this.emit();
    return true;
  }

  invalidate() {
    this.epoch++;
    this.clearImages();
    this.entries.clear();
    void cleanThumbnailStorage(this.scope, true);
    this.emit();
  }

  dispose() {
    this.disposed = true;
    this.epoch++;
    this.clearImages();
    this.entries.clear();
    clearTimeout(this.timer);
    this.unsubscribe();
    this.listeners.clear();
  }

  private release(entry?: CachedShareThumbnail) {
    if (entry?.url?.startsWith('blob:')) URL.revokeObjectURL(entry.url);
  }

  private clearImages() {
    this.entries.forEach(entry => this.release(entry));
    this.downloads.forEach(request => request.controller.abort());
    this.downloads.clear();
    this.queue = [];
  }

  private prune() {
    let bytes = [...this.entries.values()].reduce((sum, entry) => sum + (entry.bytes || 0), 0);
    while (this.entries.size > MAX_CACHED_SHARE_THUMBNAILS || bytes > 32 * 1024 * 1024) {
      const id = this.entries.keys().next().value!;
      const entry = this.entries.get(id);
      bytes -= entry?.bytes || 0;
      this.release(entry);
      this.entries.delete(id);
      this.downloads.get(id)?.controller.abort();
      this.downloads.delete(id);
    }
  }

  private download(id: number, version: string, contentUrl: string, epoch: number) {
    if (this.downloads.get(id)?.version === version) return;
    this.downloads.get(id)?.controller.abort();
    const request = { controller: new AbortController(), version };
    this.downloads.set(id, request);
    const valid = () => !this.disposed && !request.controller.signal.aborted &&
      this.epoch === epoch && this.entries.get(id)?.version === version;
    this.queue.push(async () => {
      try {
        if (!valid()) return;
        const blob = await loadThumbnailBytes(this.scope, contentUrl, valid, request.controller.signal);
        if (!blob || !valid()) return;
        const current = this.entries.get(id)!;
        this.entries.set(id, { ...current, url: URL.createObjectURL(blob), bytes: blob.size });
        this.prune();
        this.emit();
      } catch (error) {
        if (valid()) console.warn('[thumbnails] Image could not be cached', error);
      } finally {
        if (this.downloads.get(id) === request) this.downloads.delete(id);
      }
    });
    this.drain();
  }

  private drain() {
    while (this.loading < 4 && this.queue.length) {
      const task = this.queue.shift()!;
      this.loading++;
      void task().finally(() => { this.loading--; this.drain(); });
    }
  }

  private emit() {
    this.armExpiry();
    this.listeners.forEach(listener => listener());
  }

  private armExpiry() {
    clearTimeout(this.timer);
    if (!this.listeners.size || this.disposed) return;
    const now = Date.now();
    const next = Math.min(...[...this.entries.values()]
      .filter(entry => entry.url && entry.expiresAt > now).map(entry => entry.expiresAt));
    if (Number.isFinite(next)) this.timer = setTimeout(() => this.emit(), Math.max(1, next - now));
  }
}

const caches = new WeakMap<QueryClient, ShareThumbnailCache>();

export function getShareThumbnailCache(client: QueryClient, scope: string): ShareThumbnailCache {
  let cache = caches.get(client);
  if (!cache || cache.scope !== scope) {
    cache?.dispose();
    // Auth may still be loading during render. Only AuthProvider's resolved
    // signed-out state may erase the previous session's persisted images.
    if (!scope.startsWith('anonymous:')) void cleanThumbnailStorage(scope);
    cache = new ShareThumbnailCache(client, scope);
    caches.set(client, cache);
  }
  return cache;
}

/** Runs even when no thumbnail UI is mounted, notably during logout. */
export function setShareThumbnailAccount(client: QueryClient, scope: string | null) {
  const cache = caches.get(client);
  if (cache && cache.scope !== scope) {
    cache.dispose();
    caches.delete(client);
  }
  void cleanThumbnailStorage(scope);
}

export function invalidateShareThumbnailCache(client: QueryClient) {
  caches.get(client)?.invalidate();
}